import { useAuthStore } from './store';

/**
 * The one place PH10 decides what a 401 means — slice-3560 (ADR-0046).
 *
 * <p>Before this slice the rule was `useAuthStore.getState().clear()`, duplicated verbatim in
 * `shared/api/client.ts` and five bespoke multipart/blob fetch wrappers, and it was **silent**:
 * the store emptied, zustand subscribers re-rendered, `RequireAuth` navigated to `/login`, and
 * the operator saw a login form with no explanation and their typing gone. Two things change:
 *
 * <ol>
 *   <li>the clear carries a <b>reason</b>, which `LoginPage` renders — and `RequireAuth`
 *       already passes the current location as `state.from`, so after signing back in the user
 *       lands where they were;</li>
 *   <li>a <b>kiosk</b> session is never cleared here. A break-room display must not be dumped
 *       on a login form by one transient 401 that nobody is standing there to fix; instead the
 *       session guard is asked to renew, and the display recovers on its own.</li>
 * </ol>
 */
export const SESSION_EXPIRED_MESSAGE =
  'Your session expired. Sign in again to pick up where you left off.';

type KioskRecovery = () => void;

let kioskRecovery: KioskRecovery | null = null;

/**
 * Registered once by `useSessionExpiry` so `handleUnauthorized` can ask for a renew without
 * importing React state into the fetch layer. Returns an unregister function.
 */
export function registerKioskRecovery(fn: KioskRecovery): () => void {
  kioskRecovery = fn;
  return () => {
    if (kioskRecovery === fn) kioskRecovery = null;
  };
}

/**
 * Call on a 401 that came back while a token was attached. No-op when already signed out
 * (a 401 on a public endpoint is not a session event).
 */
export function handleUnauthorized(): void {
  const { session, clear } = useAuthStore.getState();
  if (!session) return;
  if (session.kiosk) {
    kioskRecovery?.();
    return;
  }
  clear(SESSION_EXPIRED_MESSAGE);
}
